import { io } from 'socket.io-client';

/**
 * Admin must connect to the SAME server that serves /api (and /api/verify-face).
 * Listeners are stored on the service so a reconnect does not drop heartbeat/presence handlers.
 */
class WebsocketService {
  constructor() {
    this.socket = null;
    this.listeners = {};
    this._anyListeners = [];
  }

  _attach(type, callback) {
    if (!this.socket) return;
    if (type === '*') {
      this.socket.onAny(callback);
    } else {
      this.socket.on(type, callback);
    }
  }

  _attachAll() {
    Object.keys(this.listeners).forEach((type) => {
      (this.listeners[type] || []).forEach((cb) => this._attach(type, cb));
    });
    this._anyListeners.forEach((cb) => this._attach('*', cb));
  }

  connect(examId = null, userId = null, role = null) {
    if (this.socket) {
      try { this.socket.disconnect(); } catch (_) { /* ignore */ }
    }
    const sameOrigin = (typeof window !== 'undefined' && window.location && window.location.origin) ? window.location.origin : null;
    const runtimeUrl = (typeof window !== 'undefined' && window.VIRGIL_WS_URL) ? window.VIRGIL_WS_URL : null;
    const envUrl = process.env.REACT_APP_WEBSOCKET_URL || process.env.VIRGIL__WEBSOCKET_URL || null;
    const fallbackUrl = sameOrigin || 'https://virgil.cpslearn.co.za';
    let url = sameOrigin || runtimeUrl || envUrl || fallbackUrl;
    if (typeof url === 'string' && url.startsWith('wss://')) {
      url = url.replace(/^wss:\/\//, 'https://');
    } else if (typeof url === 'string' && url.startsWith('ws://')) {
      url = url.replace(/^ws:\/\//, 'http://');
    }

    const query = {};
    if (role) query.role = role;
    if (examId) query.examId = examId;
    if (userId) query.userId = userId;

    console.log('[Proctoring][WS] connect', { url, query: { role, examId, userId }, sameOrigin: !!sameOrigin });
    this.socket = io(url, { query, path: '/socket.io' });
    this._attachAll();
    this.socket.on('connect', () => {
      console.log('[Proctoring][WS] connected', { id: this.socket.id, url, examId: query.examId });
      if (query.examId) {
        try { this.socket.emit('request_connected', { examId: query.examId }); } catch (_) { /* ignore */ }
      }
    });
    this.socket.on('connect_error', (err) => {
      console.error('[Proctoring][WS] connect_error', { message: err?.message, url, query });
    });
    this.socket.on('error', (err) => {
      console.error('[Proctoring][WS] error', err);
    });
    this.socket.on('disconnect', (reason) => {
      console.log('[Proctoring][WS] disconnect', { reason, url });
    });
    this.socket.onAny((type, ...args) => {
      if (type !== 'webcam_frame' && type !== 'screen_frame') {
        const arg0 = args?.[0];
        const preview = arg0 != null ? (typeof arg0 === 'object' ? JSON.stringify(arg0).slice(0, 200) : String(arg0)) : '';
        console.log('[WS] <-', type, preview);
      }
    });
  }

  sendMessage(type, payload) {
    if (this.socket) {
      console.log('[Proctoring][WS] ->', type, payload != null ? (typeof payload === 'object' ? JSON.stringify(payload).slice(0, 150) : payload) : '');
      this.socket.emit(type, payload);
    } else {
      console.warn('[Proctoring][WS] sendMessage no socket', type);
    }
  }

  subscribe(type, callback) {
    if (type === '*') {
      this._anyListeners.push(callback);
    } else {
      if (!this.listeners[type]) this.listeners[type] = [];
      this.listeners[type].push(callback);
    }
    this._attach(type, callback);
    return () => {
      if (type === '*') {
        this._anyListeners = this._anyListeners.filter((fn) => fn !== callback);
        if (this.socket) this.socket.offAny(callback);
      } else {
        this.listeners[type] = (this.listeners[type] || []).filter((fn) => fn !== callback);
        if (this.socket) this.socket.off(type, callback);
      }
    };
  }
}

const websocketService = new WebsocketService();
export default websocketService;
